import { Button, CheckboxField, DrawerBody, DrawerFooter, Icon, SubmitButton, TextField } from "@dgs/core";
import { FieldArray, FieldArrayRenderProps, Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CountrySelectField } from "../../../../shared/ui/CountrySelectField";
import { AdditionalList, AdditionalListItem, EmptyAdditionalList } from "../../contacts/create/AdditionalList";
import { useDefaultCountry } from "../../shared/hooks";
import { CountryOptionResource } from "../../shared/types";
import { CreateCompanyStepForm, DrawerFormWithSubmitter, FormActions, SubmitAction } from "./Form";

interface AddressFormValues {
	city: string;
	zip: string;
	houseNumber: string;
	street: string;
	additionalInfo: string;
	country: CountryOptionResource | null;
	isPrimary: boolean;
}

const emptyAddressFormValues: AddressFormValues = {
	city: "",
	zip: "",
	houseNumber: "",
	street: "",
	additionalInfo: "",
	country: null,
	isPrimary: false,
};

export interface AddressesFormValues {
	addresses: AddressFormValues[];
	action: SubmitAction;
}

export const emptyAddressesFormValues: AddressesFormValues = {
	addresses: [],
	action: "next",
};

interface Props extends CreateCompanyStepForm<AddressesFormValues> {
	onGoBack: () => void;
}

const ZipCodeAndCity = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const StreetAndHouseNumber = styled.div`
	display: grid;
	grid-template-columns: 3fr 1fr;
	gap: ${({ theme }) => theme.spacing(2)};
`;

export const AddressesForm: FC<Props> = ({ onGoBack, initialValues, onSubmit }) => {
	const { t } = useTranslation();
	const { defaultCountry } = useDefaultCountry();

	const validate = useCallback(
		(values: AddressesFormValues) => {
			const errors: { [K in keyof AddressesFormValues]?: any } = {};
			values.addresses.forEach((address, index) => {
				const addressErrors: { [K in keyof AddressFormValues]?: any } = {};
				if (!address.street) {
					addressErrors.street = t("Required field");
				}
				if (!address.houseNumber) {
					addressErrors.houseNumber = t("Required field");
				}
				if (!address.zip) {
					addressErrors.zip = t("Required field");
				}
				if (!address.city) {
					addressErrors.city = t("Required field");
				}
				if (!address.country) {
					addressErrors.country = t("Required field");
				}

				if (Object.keys(addressErrors).length > 0) {
					errors.addresses = { ...(errors.addresses || {}), [index]: addressErrors };
				}
			});

			return errors;
		},
		[t],
	);

	const handleDelete = (props: FieldArrayRenderProps, index: number) => {
		props.remove(index);
		const remaining = (props.form.values as AddressesFormValues).addresses.filter((_, i) => i !== index);

		if (remaining.length > 0 && !remaining.some((a) => a.isPrimary)) {
			void props.form.setFieldValue(`${props.name}[0].isPrimary`, true);
		}
	};

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			<DrawerFormWithSubmitter>
				<DrawerBody>
					<FieldArray name="addresses">
						{(props) => (
							<>
								<AdditionalList>
									{(props.form.values as AddressesFormValues).addresses.map((_, index) => (
										<AdditionalListItem key={index} onDelete={() => handleDelete(props, index)}>
											<TextField name={`${props.name}[${index}].additionalInfo`} label={t("Additional info")} />
											<StreetAndHouseNumber>
												<TextField name={`${props.name}[${index}].street`} label={t("Street")} required />
												<TextField name={`${props.name}[${index}].houseNumber`} label={t("House number")} required />
											</StreetAndHouseNumber>
											<ZipCodeAndCity>
												<TextField name={`${props.name}[${index}].zip`} label={t("Zip")} required />
												<TextField name={`${props.name}[${index}].city`} label={t("City")} required />
											</ZipCodeAndCity>
											<CountrySelectField name={`${props.name}[${index}].country`} label={t("Country")} required />
											<CheckboxField
												label={t("Mark as primary")}
												name={`${props.name}[${index}].isPrimary`}
												disabled={(props.form.values as AddressesFormValues).addresses.length === 1}
												onChange={() => {
													props.form.values[props.name].forEach((_: any, i: number) => {
														void props.form.setFieldValue(`${props.name}[${i}].isPrimary`, false);
													});

													void props.form.setFieldValue(`${props.name}[${index}].isPrimary`, true);
												}}
											/>
										</AdditionalListItem>
									))}
								</AdditionalList>
								{(props.form.values as AddressesFormValues).addresses.length === 0 && (
									<EmptyAdditionalList>{t("No addresses available.")}</EmptyAdditionalList>
								)}
								<Button
									type="button"
									onClick={() =>
										props.push({
											...emptyAddressFormValues,
											country: defaultCountry || null,
											isPrimary: (props.form.values as AddressesFormValues).addresses.length === 0,
										})
									}
									size="small"
									prefix={<Icon $icon="plus" />}
								>
									{t("Add address")}
								</Button>
							</>
						)}
					</FieldArray>
				</DrawerBody>
				<DrawerFooter>
					<span />
					<FormActions>
						<Button type="button" onClick={onGoBack}>
							{t("Back")}
						</Button>
						<SubmitButton name="action" value="save" color="default">
							{t("Save without optionals")}
						</SubmitButton>
						<SubmitButton name="action" value="next">
							{t("Next")}
						</SubmitButton>
					</FormActions>
				</DrawerFooter>
			</DrawerFormWithSubmitter>
		</Formik>
	);
};
